export interface TimelineItem {
  id?: string | null;
  type?: string;
  content?: string | null;
  sender?: string | null;
  timestamp?: string | null;
  isTransient?: boolean;
  [key: string]: unknown;
}

// Chat rows are stored as UTC, but the timeline API serializes them without a
// timezone suffix. JavaScript otherwise interprets those values as local time.
export const timelineTimestampValue = (timestamp: string | null | undefined): number => {
  if (!timestamp) return 0;
  const isNaiveIsoTimestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timestamp)
    && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
  const value = new Date(isNaiveIsoTimestamp ? `${timestamp}Z` : timestamp).getTime();
  return Number.isFinite(value) ? value : 0;
};

const matchesPersistedMessage = (transient: TimelineItem, persisted: TimelineItem): boolean => {
  if (transient.type !== 'message' || persisted.type !== 'message') return false;
  if (transient.sender !== persisted.sender) return false;
  if ((transient.content || '').trim() !== (persisted.content || '').trim()) return false;

  // The server creates user rows before starting the agent. This narrow window
  // reconciles a client-only bubble with that row without collapsing genuine,
  // repeated messages sent later in the conversation.
  return Math.abs(timelineTimestampValue(transient.timestamp) - timelineTimestampValue(persisted.timestamp)) <= 30_000;
};

/**
 * Combine the authoritative server snapshot with only the live items that have
 * not made it to that snapshot yet. Server rows always win an ID collision.
 *
 * Do not sort the combined result here. The server snapshot is already ordered
 * by database creation order, while live items are ordered by the sequence in
 * which the UI received them. Their timestamps can come from different clocks,
 * so sorting the mixed list can move a response above the user message that
 * caused it until the next server refresh.
 */
export const mergeServerTimeline = <T extends TimelineItem>(
  serverItems: T[],
  currentItems: T[],
): T[] => {
  const serverIndexById = new Map(
    serverItems
      .map((item, index) => [String(item.id ?? ''), index] as const)
      .filter(([id]) => id !== ''),
  );
  const matchedServerIndexes = new Set<number>();
  const pendingItems = currentItems.filter((item) => {
    if (!item.isTransient) return false;
    const serverIndexForId = item.id != null
      ? serverIndexById.get(String(item.id))
      : undefined;
    if (serverIndexForId !== undefined) {
      matchedServerIndexes.add(serverIndexForId);
      return false;
    }

    const matchingServerIndex = serverItems.findIndex(
      (serverItem, index) =>
        !matchedServerIndexes.has(index) &&
        matchesPersistedMessage(item, serverItem),
    );
    if (matchingServerIndex >= 0) {
      matchedServerIndexes.add(matchingServerIndex);
      return false;
    }
    return true;
  });

  return [...serverItems, ...pendingItems];
};

export const getTransientTimelineItems = <T extends TimelineItem>(items: T[]): T[] =>
  items.filter(item => item.isTransient);
